import WooFilterApplyButtonComponent from "./woo-filter-apply-button-component.js";
import WooFilterApplyFloatingButtonComponent from "./woo-filter-apply-floating-button-component.js";
import WooFilterBoxComponent from "./woo-filter-box-component.js";
import ProductsService from "../services/products-service.js";

export default class WooFilterComponent {
  constructor() {
    this.filterBoxes = [];
    this.selected = {};
    this.oldSelection = {};
    this.applyButton = null;
    this.applyFloatingButton = null;
  }

  run() {
    const applyButtonNode = document.getElementById("ds-woo-filter__apply-button");
    const floatingContainerNode = document.querySelector(".ds-woo-filter__apply-filter-container");

    if (!applyButtonNode || !floatingContainerNode) {
      return;
    }

    this.applyButton = new WooFilterApplyButtonComponent(applyButtonNode);
    this.applyButton.onClick = () => {
      this.handleApplyClick();
    };

    this.applyFloatingButton = new WooFilterApplyFloatingButtonComponent(floatingContainerNode);
    this.applyFloatingButton.onClick = () => {
      this.handleApplyClick();
    };

    ProductsService.setEndpointConfig({
      actionParam: floatingContainerNode.getAttribute("data-action-param") || "",
      filterArgsParam: floatingContainerNode.getAttribute("data-filter-args-param") || "",
      actionValue: floatingContainerNode.getAttribute("data-action-value") || "",
    });

    this.findFilterBox();
    this.hydrateFromUrl();
  }

  findFilterBox() {
    const boxes = document.querySelectorAll(".ds-woo-filter__box");

    for (let i = 0; i < boxes.length; i += 1) {
      const filterBox = new WooFilterBoxComponent(boxes[i]);
      filterBox.onChange = (checkbox) => {
        this.handleChange(checkbox);
      };

      this.filterBoxes.push(filterBox);
    }
  }

  handleApplyClick() {
    this.oldSelection = this.cloneSelection(this.selected);
    this.makeFilterUrl();
  }

  async handleChange(checkbox) {
    this.selected = this.collectSelectedFromBoxes();

    if (this.deepCompare(this.selected, this.oldSelection)) {
      ProductsService.abortProductsCount();
      this.applyButton.disable();
      this.applyFloatingButton.hide();
      return;
    }

    this.applyButton.pending();
    this.applyFloatingButton.pending();
    this.positionFloatingButton(checkbox);

    const count = await this.makeCountRequest();
    if (count < 0) {
      return;
    }

    this.applyButton.setProductsCount(count);
    this.applyFloatingButton.setProductsCount(count);
  }

  prepareFilterArgs(args) {
    const normalized = {};

    Object.keys(args).forEach((taxonomyKey) => {
      normalized[taxonomyKey] = [];

      args[taxonomyKey].forEach((row) => {
        if (row?.slug) {
          normalized[taxonomyKey].push(row.slug);
        }
      });
    });

    return normalized;
  }

  async makeCountRequest() {
    ProductsService.setFilterArgs(this.prepareFilterArgs(this.selected));
    return ProductsService.countFilteredProducts();
  }

  hydrateFromUrl() {
    const parsed = this.parseSelectionFromUrl();
    if (Object.keys(parsed).length === 0) {
      this.selected = this.collectSelectedFromBoxes();
      this.oldSelection = this.cloneSelection(this.selected);
      return;
    }

    for (let i = 0; i < this.filterBoxes.length; i += 1) {
      const filterBox = this.filterBoxes[i];
      const slug = filterBox.getSlug();
      if (!slug) {
        continue;
      }

      const values = parsed[slug] || [];
      filterBox.setCheckedBySlugs(values);
    }

    this.selected = this.collectSelectedFromBoxes();
    this.oldSelection = this.cloneSelection(this.selected);
  }

  parseSelectionFromUrl() {
    const out = {};
    const params = new URLSearchParams(window.location.search);

    for (const [key, value] of params.entries()) {
      if (!key.startsWith("filter_")) {
        continue;
      }

      const taxonomyKey = key.slice(7);
      if (!taxonomyKey) {
        continue;
      }

      const slugs = value
        .split(",")
        .map((slug) => slug.trim())
        .filter((slug) => slug !== "");

      if (slugs.length > 0) {
        out[taxonomyKey] = slugs;
      }
    }

    return out;
  }

  collectSelectedFromBoxes() {
    const selected = {};

    for (let i = 0; i < this.filterBoxes.length; i += 1) {
      const filterBox = this.filterBoxes[i];
      const slug = filterBox.getSlug();
      const checked = filterBox.getChecked();

      if (!slug || checked.length === 0) {
        continue;
      }

      selected[slug] = checked;
    }

    return selected;
  }

  cloneSelection(selection) {
    return JSON.parse(JSON.stringify(selection || {}));
  }

  makeFilterUrl() {
    const filterPrefix = "filter_";
    const currentUrl = new URL(window.location.href);

    Array.from(currentUrl.searchParams.keys()).forEach((key) => {
      if (key.startsWith(filterPrefix)) {
        currentUrl.searchParams.delete(key);
      }
    });

    Object.keys(this.selected).forEach((taxonomyKey) => {
      const values = this.selected[taxonomyKey] || [];
      const slugs = values.map((item) => item.slug).filter((slug) => !!slug);
      if (slugs.length > 0) {
        currentUrl.searchParams.set(`${filterPrefix}${taxonomyKey}`, slugs.join(","));
      }
    });

    window.location.href = currentUrl.toString();
  }

  positionFloatingButton(checkbox) {
    if (!checkbox?.obj || !this.applyFloatingButton?.obj) {
      return;
    }

    const rect = checkbox.obj.getBoundingClientRect();
    const absoluteTop = rect.top + window.scrollY;
    const buttonRect = this.applyFloatingButton.obj.getBoundingClientRect();
    this.applyFloatingButton.obj.style.top = `${absoluteTop - buttonRect.height}px`;
  }

  deepCompare(obj1, obj2) {
    if (obj1 === obj2) {
      return true;
    }

    if (typeof obj1 !== "object" || typeof obj2 !== "object" || !obj1 || !obj2) {
      return false;
    }

    if (Array.isArray(obj1) !== Array.isArray(obj2)) {
      return false;
    }

    if (Array.isArray(obj1)) {
      if (obj1.length !== obj2.length) {
        return false;
      }

      for (let i = 0; i < obj1.length; i += 1) {
        if (!this.deepCompare(obj1[i], obj2[i])) {
          return false;
        }
      }

      return true;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (let i = 0; i < keys1.length; i += 1) {
      const key = keys1[i];
      if (!Object.prototype.hasOwnProperty.call(obj2, key)) {
        return false;
      }

      if (!this.deepCompare(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  }
}
